import { useEffect, useMemo, useState, useRef, FormEvent } from 'react';
import { useParams } from 'react-router';
import { v4 as uuidv4 } from 'uuid';
import { NotFound } from '../../screens/notFound';
import { MessageView } from '../../components/messages/MessageView';
import { MessageBubble, getMessageChannel } from '../../components/messages/MessageBubble';
import { PhoneCallView } from '../../components/phoneCalls/PhoneCallView';
import { ConversationSubtitle } from '../../components/conversations/ConversationSubtitle';
import './show.scss';
import { useAppDispatch, useAppSelector } from '../../hooks/reduxToolkit';
import { selectConversationById, selectConversationTimeline } from '../../reduxToolkit/conversationsSelectors';
import { addMessage, updateConversation } from '../../reduxToolkit/conversationsSlice';

export function ConversationShow() {
  const { id } = useParams<{ id: string }>();
  const dispatch = useAppDispatch();
  const { membershipId } = useAppSelector((s) => s.membership);
  const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set());
  const [newMessage, setNewMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Get conversation data from Redux
  const conversation = useAppSelector((state) => selectConversationById(state, id || ''));
  const timeline = useAppSelector((state) => selectConversationTimeline(state, id || ''));
  const provider = useAppSelector(s => s.providers.providers.find(p => p.id === conversation?.provider_id));

  const isProviderChat = conversation?.topic_type === 'alice-provider-chat';

  // The member's newest reply to Alice carries the delivery receipt, same as in
  // the Alice conversation view.  Provider email is not the member writing.
  const lastMemberMessageId = useMemo(() => {
    for (let i = timeline.length - 1; i >= 0; i--) {
      const item = timeline[i];
      if (item.type !== 'message') continue;
      if (item.data.direction === 'INBOUND' && getMessageChannel(item.data) === 'in-app') {
        return item.data.id;
      }
    }
    return undefined;
  }, [timeline]);

  // Expand the most recent item by default
  useEffect(() => {
    if (timeline.length > 0 && expandedItems.size === 0) {
      const mostRecentItem = timeline[timeline.length - 1];
      setExpandedItems(new Set([mostRecentItem.data.id]));
    }
  }, [timeline, expandedItems.size]);

  const toggleItemExpansion = (itemId: string) => {
    setExpandedItems(prev => {
      const newSet = new Set(prev);
      if (newSet.has(itemId)) {
        newSet.delete(itemId);
      } else {
        newSet.add(itemId);
      }
      return newSet;
    });
  };

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();

    if (!newMessage.trim() || isSubmitting) return;

    setIsSubmitting(true);

    const now = new Date().toISOString();
    const messageId = uuidv4();

    dispatch(
      addMessage({
        id: messageId,
        conversation_id: conversation!.id,
        membership_id: membershipId,
        direction: 'INBOUND',
        channel: 'in-app',
        body_text: newMessage.trim(),
        body_html: null,
        subject: null,
        from_address: null,
        to_address: null,
        status: 'DELIVERED',
        sent_at: now,
        read_at: null,
        scheduled_at: null,
        attachments: null,
        created_at: now,
        updated_at: now,
        deleted_at: null,
        error: null,
        external_message_id: null,
        headers: null,
      })
    );

    dispatch(
      updateConversation({
        id: conversation!.id,
        updated_at: now,
        last_message_at: now,
        last_message_direction: 'INBOUND',
        ...(conversation!.status === 'CLOSED' ? { status: 'OPEN' as const } : {}),
      })
    );

    setNewMessage('');
    setIsSubmitting(false);
    textareaRef.current?.focus();
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e as unknown as FormEvent);
    }
  };

  if (!conversation) {
    return <NotFound />;
  }

  const isClosed = conversation.status === 'CLOSED';

  const handleClose = () => {
    const now = new Date().toISOString();
    dispatch(
      updateConversation({
        id: conversation.id,
        updated_at: now,
        status: 'CLOSED',
      })
    );
  };

  const handleReopen = () => {
    const now = new Date().toISOString();
    dispatch(
      updateConversation({
        id: conversation.id,
        updated_at: now,
        status: 'OPEN',
      })
    );
  };

  return (
    <div className="conversation-show">
      <div className="conversation-header">
        <div className="d-flex justify-content-between align-items-start">
          <h2 className="conversation-title">
            Conversation with {provider?.name || 'Provider'}
            {isClosed && <span className="badge bg-secondary ms-2">Closed</span>}
          </h2>
          {!isClosed && (
            <button
              type="button"
              className="btn btn-outline-secondary btn-sm"
              onClick={handleClose}
            >
              Close
            </button>
          )}
        </div>
        <ConversationSubtitle conversation={conversation} messages={timeline.filter((t): t is { type: 'message'; data: import('../../../types/supabase').MessageRow } => t.type === 'message').map(t => t.data)} />
      </div>

      {isClosed && (
        <div className="conversation-show__closed-banner d-flex justify-content-between align-items-center">
          <span>This conversation is closed.</span>
          <button
            type="button"
            className="btn btn-outline-primary btn-sm"
            onClick={handleReopen}
          >
            Reopen
          </button>
        </div>
      )}

      <div className="timeline">
        {timeline.length === 0 ? (
          <div className="no-items">
            <p>No messages or phone calls in this conversation yet.</p>
          </div>
        ) : (
          timeline.map((item) => {
            const isExpanded = expandedItems.has(item.data.id);

            if (item.type === 'message') {
              // In-app messages (Alice <-> member) render as chat bubbles so
              // they're visually distinct from outbound provider emails.
              if (getMessageChannel(item.data) === 'in-app') {
                return (
                  <MessageBubble
                    key={item.data.id}
                    message={item.data}
                    showDeliveryReceipt={item.data.id === lastMemberMessageId}
                  />
                );
              }
              return (
                <MessageView
                  key={item.data.id}
                  message={item.data}
                  isExpanded={isExpanded}
                  onToggle={() => toggleItemExpansion(item.data.id)}
                />
              );
            } else if (item.type === 'phone_call') {
              return (
                <PhoneCallView
                  key={item.data.id}
                  phoneCall={item.data}
                  isExpanded={isExpanded}
                  onToggle={() => toggleItemExpansion(item.data.id)}
                />
              );
            }
            return null;
          })
        )}
      </div>

      {isProviderChat && (
        <form className="conversation-show__reply" onSubmit={handleSubmit}>
          <div className="conversation-show__reply-label">
            Reply to Alice
          </div>
          <div className="conversation-show__reply-wrapper">
            <textarea
              ref={textareaRef}
              className="form-control"
              placeholder="Send a message to Alice about this conversation..."
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              onKeyDown={handleKeyDown}
              rows={2}
              disabled={isSubmitting}
            />
            <button
              type="submit"
              className="btn btn-primary conversation-show__send-button"
              disabled={!newMessage.trim() || isSubmitting}
            >
              <i className="material-icons">send</i>
            </button>
          </div>
          <div className="conversation-show__reply-hint">
            Press Enter to send, Shift+Enter for new line
          </div>
        </form>
      )}
    </div>
  );
}
